/**
 * [PM-SG 2026-08-11] Shared furniture for the Grand Prix board — the palette, the car, the
 * flag, and the keyframes more than one view needs.
 *
 * The board is DELIBERATELY not theme-aware. Every other section of the portal follows the
 * light/dark switch; this one is a wall display in a fixed livery, and a light-mode Formula 1
 * board would be a different product. The colours below are literals for that reason.
 */
import React from 'react';
import styled, { keyframes, css } from 'styled-components';

/* ─────────────────────────────────────────────────────────────── palette ── */

export const INK = '#15151E';
export const INK_2 = '#1D1D28';
export const ASPHALT_2 = '#2C2C33';
/**
 * [PM-SG 2026-08-12] The lane surface on the race page.
 *
 * Darker than ASPHALT_2, and the reason is a consequence of removing the seam: the asphalt used
 * to cover only the track cell, so a lighter grey read as one panel among several. Now that the
 * whole lane is a single continuous surface, that same grey covers most of the screen — and at
 * ASPHALT_2 the board came out bright enough to glow in a dark room, which is exactly where a
 * wall display lives.
 */
export const LANE_SURFACE = '#212129';
export const RED = '#E10600';
export const PURPLE = '#B026FF';
export const AMBER = '#FFB800';
export const GREEN = '#00D26A';
export const LINE = 'rgba(255,255,255,.10)';
export const EASE = 'cubic-bezier(.22,.61,.36,1)';

/** Every text slot on the board is white by request — no grey copy anywhere. Hierarchy is
 *  carried by size and weight instead, which is what survives being read across a room. */
export const TEXT = '#FFFFFF';

export const FONT = "'Titillium Web', system-ui, -apple-system, sans-serif";

/* ───────────────────────────────────────────────────────────── keyframes ── */

export const rowIn = keyframes`from { opacity: 0; transform: translateX(-26px); }`;
export const idle = keyframes`0%,100% { transform: translateY(0); } 50% { transform: translateY(.5px); }`;
export const kerbRun = keyframes`from { transform: translateX(-200px); } to { transform: translateX(calc(1920px + 200px)); }`;

export const fog = keyframes`
  0%   { transform: translate(0,0) scale(.3); opacity: 0; }
  14%  { opacity: var(--fogop,.85); }
  100% { transform: translate(-104px, var(--dy,-5px)) scale(2.7); opacity: 0; }
`;

/** The kerbing that frames the track and the pit wall — red/white on the diagonal, with one
 *  slow highlight travelling along it. That highlight is the cheapest "this screen is on" cue
 *  there is, and on a board that must not otherwise move it does a lot of work. */
export const kerb = css`
  position: relative;
  overflow: hidden;
  background: repeating-linear-gradient(115deg, ${RED} 0 16px, #F2F2F2 16px 32px);
  opacity: 0.85;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 180px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
    animation: ${kerbRun} 9s linear infinite;
    will-change: transform;
  }
`;

/* ──────────────────────────────────────────────────────────────── the car ── */

/* ────────────────────────────────────────────────── the side-view car ── */

/**
 * [PM-SG 2026-08-11] F1 car in SIDE profile, nose to the RIGHT — the hero object of the new
 * board, replacing the top-down icon.
 *
 * Why side-on. Top-down is a diagram: it tells you where something is on a map. A profile is a
 * PORTRAIT — it has a stance, a silhouette you recognise from across a room, and it reads as
 * travelling left-to-right, which is the direction every progress bar on this board already
 * runs. It is also the view every piece of F1 merchandise, livery art and team graphic uses,
 * for exactly that reason.
 *
 * Drawn as compound curves rather than rounded rectangles. The old car was twelve rects and
 * three straight-line paths, which is why it read as pixel art; every major surface here is a
 * Bézier, and the proportions follow a real car — roughly 3:1, front wing lower and wider than
 * the rear, rear tyre larger than the front.
 *
 * MATERIAL, not opacity. Livery colour arrives as `currentColor` and knows nothing about the
 * shading, which is applied as black and white gradients on top. That means any of the eleven
 * liveries lights correctly without a per-colour variant, and nothing goes muddy the way
 * flat opacity did on dark asphalt.
 */
export const SideCarSvg: React.FC<{ idSuffix?: string }> = ({ idSuffix = '' }) => {
  const gid = `pmsgBody${idSuffix}`;
  const sid = `pmsgSheen${idSuffix}`;
  const tid = `pmsgTyre${idSuffix}`;
  return (
    <svg viewBox="0 0 200 64" aria-hidden="true">
      <defs>
        {/* Light from above: a bright crest along the top surfaces, shadow gathering under the
            floor. Applied over the livery, so it works for every colour. */}
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFF" stopOpacity="0.34" />
          <stop offset="38%" stopColor="#FFF" stopOpacity="0.06" />
          <stop offset="62%" stopColor="#000" stopOpacity="0.10" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.42" />
        </linearGradient>
        {/* A single specular streak along the engine cover — the one highlight that makes
            bodywork read as painted rather than filled. */}
        <linearGradient id={sid} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#FFF" stopOpacity="0" />
          <stop offset="45%" stopColor="#FFF" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#FFF" stopOpacity="0" />
        </linearGradient>
        <radialGradient id={tid} cx="0.38" cy="0.32" r="0.85">
          <stop offset="0%" stopColor="#3A3A44" />
          <stop offset="55%" stopColor="#17171D" />
          <stop offset="100%" stopColor="#0B0B0F" />
        </radialGradient>
      </defs>

      {/* contact shadow — grounds the car instead of letting it float */}
      <ellipse cx="102" cy="55" rx="72" ry="4" fill="#000" opacity="0.45" />

      {/* ── rear wing ────────────────────────────────────────────────────────────────────
          A HIGH horizontal plane on a slim pylon, not a boxy slab. The first attempt drew the
          endplate as a tall rounded rect with the planes crossing it, which rendered as a small
          flag on a stick — the shape a viewer reads first, and it was reading wrong. */}
      <path d="M27 30 L33 30 L31 20 L29 20 Z" fill="currentColor" opacity=".55" />
      <path d="M17 11 C26 9.5 38 9.5 47 11 L47 16 C38 14.6 26 14.6 17 16 Z" fill="currentColor" />
      <path d="M19 5.5 C27 4.4 37 4.4 44 5.5 L44 9.4 C37 8.4 27 8.4 19 9.4 Z"
        fill="currentColor" opacity=".82" />
      <path d="M15 4 h4.4 a1.5 1.5 0 0 1 1.5 1.5 v14 a1.5 1.5 0 0 1 -1.5 1.5 h-4.4
               a1.5 1.5 0 0 1 -1.5 -1.5 v-14 a1.5 1.5 0 0 1 1.5 -1.5 z"
        fill="currentColor" opacity=".95" />

      {/* ── floor ── */}
      <path d="M34 41 L168 41 L168 47 C122 48.6 78 48.6 38 47 Z" fill="currentColor" opacity=".55" />

      {/* ── main body: rear deck → airbox → cockpit surround → nose ────────────────────── */}
      <path d="M31 39
               C38 37.4 45 35.6 52 33.4
               C60 30.6 64.5 25 70 18.6
               C74 14.2 80 12 87 12
               L99 12.4
               C105.5 13.4 109 17 110.6 22.6
               L113 29
               C134 30.6 156 34.4 180 39.4
               L193 41.6
               L193 46.6
               C140 48.2 84 48.2 31 46.2 Z"
        fill="currentColor" />
      <path d="M31 39
               C38 37.4 45 35.6 52 33.4
               C60 30.6 64.5 25 70 18.6
               C74 14.2 80 12 87 12
               L99 12.4
               C105.5 13.4 109 17 110.6 22.6
               L113 29
               C134 30.6 156 34.4 180 39.4
               L193 41.6
               L193 46.6
               C140 48.2 84 48.2 31 46.2 Z"
        fill={`url(#${gid})`} />
      {/* specular crest along the engine cover — the highlight that makes it read as painted */}
      <path d="M70 19.4 C75 14.6 81 13 87 13 L98 13.4 C104 14.4 107 17.4 108.6 21.6
               L104 22.6 C102 19 99.6 17.6 96 17.4 L88 17 C82 17 77.6 18.6 74 22.6 Z"
        fill={`url(#${sid})`} />

      {/* ── sidepod: the undercut is what gives the body two planes instead of one ── */}
      <path d="M64 27 L100 26 C108 27.6 112 32 113 39.4 L113 46 L64 46 Z" fill="#000" opacity=".25" />
      <path d="M68 28.4 L98 27.6 C105 29 108.4 32.4 109.4 38 L68 39 Z" fill="currentColor" opacity=".45" />

      {/* ── cockpit + halo ── */}
      <ellipse cx="100" cy="21.6" rx="7.6" ry="4.2" fill="#0B0B0F" />
      <path d="M95 25.6 C96.6 18.6 105 15.6 113 17 C120 18.2 124.6 22.4 125 27.4"
        fill="none" stroke="#0B0B0F" strokeWidth="3" strokeLinecap="round" />

      {/* ── nose cone + front wing: lower and WIDER than the rear, as on a real car ── */}
      <path d="M172 39 L196 36.6 C198 36.6 199.4 37.6 199.4 39.4 L199.4 44
               C199.4 45.8 198 46.8 196 46.8 L172 45.6 Z" fill="currentColor" />
      <path d="M172 39 L196 36.6 C198 36.6 199.4 37.6 199.4 39.4 L199.4 44
               C199.4 45.8 198 46.8 196 46.8 L172 45.6 Z" fill={`url(#${gid})`} />
      <path d="M189 32 h5 a1.6 1.6 0 0 1 1.6 1.6 v17 a1.6 1.6 0 0 1 -1.6 1.6 h-5
               a1.6 1.6 0 0 1 -1.6 -1.6 v-17 a1.6 1.6 0 0 1 1.6 -1.6 z"
        fill="currentColor" opacity=".92" />

      {/* ── tyres. Made substantially larger: an F1 wheel is nearly a third of the car's height,
             and at the previous radius they read as castors under a slipper. ── */}
      <circle cx="56" cy="36" r="19" fill={`url(#${tid})`} />
      <circle cx="56" cy="36" r="19" fill="none" stroke="#000" strokeWidth="1.3" opacity=".6" />
      <circle cx="56" cy="36" r="8.4" fill="#23232D" />
      <circle cx="56" cy="36" r="8.4" fill="none" stroke="currentColor" strokeWidth="2" opacity=".9" />
      <path d="M44 24.6 A19 19 0 0 1 63.4 20.4" fill="none" stroke="#FFF" strokeWidth="1.8"
        opacity=".18" strokeLinecap="round" />

      <circle cx="162" cy="37.6" r="17" fill={`url(#${tid})`} />
      <circle cx="162" cy="37.6" r="17" fill="none" stroke="#000" strokeWidth="1.2" opacity=".6" />
      <circle cx="162" cy="37.6" r="7.4" fill="#23232D" />
      <circle cx="162" cy="37.6" r="7.4" fill="none" stroke="currentColor" strokeWidth="1.8" opacity=".9" />
      <path d="M151.4 27.4 A17 17 0 0 1 168.6 23.6" fill="none" stroke="#FFF" strokeWidth="1.6"
        opacity=".18" strokeLinecap="round" />
    </svg>
  );
};

/* ──────────────────────────────────────────────────────── the exhaust ── */

/**
 * [PM-SG 2026-08-12] The plume behind the car.
 *
 * Six puffs, each on its own drift, born at the diffuser and dying about a car's length behind.
 * They grow as they go and fade on a sine rather than linearly, which is what stops a puff
 * winking out at full brightness. Negative animation delays mean the trail is already mid-flow
 * on the very first frame — a plume that starts empty and fills reads as the page loading, not
 * as an engine running.
 *
 * ONE blur on the container rather than one per puff: six filter passes per car times ten cars,
 * running for hours on a wall display, is the kind of thing that quietly cooks a television's
 * media player. The blur is what turns six hard circles into smoke.
 *
 * This is LIFE, not data. Every car has it, classified or not — a car with nothing coming off
 * the back reads as parked however much else on the screen is moving — and it deliberately says
 * nothing about anybody's numbers.
 */
export const exhaustPuff = keyframes`
  0%   { transform: translate(0, 0) scale(.34); opacity: 0; }
  12%  { opacity: var(--puff-max, .8); }
  55%  { opacity: calc(var(--puff-max, .8) * .55); }
  100% { transform: translate(calc(var(--puff-run, 92px) * -1), var(--dy, -6px)) scale(2.9); opacity: 0; }
`;

export const Exhaust = styled.div`
  position: absolute;
  right: calc(100% - 9%);
  top: 56%;
  width: 0;
  height: 0;
  z-index: 1;
  pointer-events: none;
  /* one pass for the whole plume, not one per puff */
  filter: blur(4px);

  i {
    position: absolute;
    right: 0;
    top: 0;
    width: var(--puff-size, 26px);
    height: var(--puff-size, 26px);
    margin: calc(var(--puff-size, 26px) / -2) calc(var(--puff-size, 26px) / -2) 0 0;
    border-radius: 50%;
    opacity: 0;
    background: radial-gradient(circle,
      rgba(236, 240, 250, 0.62) 0%,
      rgba(214, 220, 236, 0.34) 34%,
      rgba(204, 210, 226, 0.12) 62%,
      rgba(204, 210, 226, 0) 80%);
    animation: ${exhaustPuff} var(--puff-dur, 2.1s) linear infinite;
    will-change: transform, opacity;
  }
  i:nth-child(1) { animation-delay: 0s;      --dy: -10px; --puff-size: 22px; }
  i:nth-child(2) { animation-delay: -0.35s;  --dy: 6px;   --puff-size: 27px; }
  i:nth-child(3) { animation-delay: -0.70s;  --dy: -4px;  --puff-size: 24px; }
  i:nth-child(4) { animation-delay: -1.05s;  --dy: 9px;   --puff-size: 30px; }
  i:nth-child(5) { animation-delay: -1.40s;  --dy: -7px;  --puff-size: 25px; }
  i:nth-child(6) { animation-delay: -1.75s;  --dy: 3px;   --puff-size: 28px; }
`;

/** The profile car's aspect. Far longer than the old 2:1 top-down icon, which is most of why
 *  that one read as a toy — a real car is about three times as long as it is tall. */
export const SIDE_CAR_RATIO = 200 / 64;

/* ────────────────────────────────────────────────────────────────── utils ── */

export const prefersReducedMotion = (): boolean =>
  !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
